// =============================================================================
// GamePage.js
// /var/www/binko-frontend/src/pages/GamePage.js
//
// Single page for the live venue trivia game.
// Polls GET /api/trivia/state every 1 second.
// Renders different UI based on phase: WAITING, QUESTION, RESULT, LEADERBOARD, BREAK
// BREAK has 3 client-side sub-views: SOCIAL (0-40s), STATS (40-80s), LEADERBOARD (80-120s)
// Match toast appears on any view when new_match detected.
// =============================================================================

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNav } from '../App';
import {
  Box, Typography, Button, LinearProgress,
  Chip, CircularProgress, Avatar, Snackbar,
  Alert, Divider, IconButton, Drawer
} from '@mui/material';
import api from '../services/api';
import TournamentTab from './TournamentTab';
import { C } from '../theme';
import { IconTrophy, IconMedal, IconCorrect, IconWrong, IconNoAnswer, IconBolt, IconFlame, IconPause, IconWave, IconStats, IconLocation, IconTick } from '../BinkoIcons';
import { ScorePops, ResultScoreCard, MatchOverlay, StreakBar } from '../components/ScoreAnimations';

// ---------------------------------------------------------------------------
// Score formatter (mirrors backend formatScore)
// ---------------------------------------------------------------------------
function formatScore(n) {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function GamePage() {
  const navigate = useNav();
  const [venueId,     setVenueId]     = useState(null);
  const [gameState,   setGameState]   = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [people,      setPeople]      = useState(null);
  const [myStats,     setMyStats]     = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [myAnswer,    setMyAnswer]    = useState(null);
  const [matchToast,  setMatchToast]  = useState(null);
  const [introSeen,   setIntroSeen]   = useState(false);
  const [activeTab,   setActiveTab]   = useState(
    () => localStorage.getItem('binko_play_tab') || 'TRIVIA'
  );
  const [breakSubView, setBreakSubView] = useState('SOCIAL'); // SOCIAL | STATS | LEADERBOARD
  const [userSelectedTab, setUserSelectedTab] = useState(null);
  const [hiSending,   setHiSending]  = useState({});

  // Persist active Play sub-tab across navigation
  useEffect(() => {
    localStorage.setItem('binko_play_tab', activeTab);
  }, [activeTab]);

  const lastPollRef   = useRef(0);
  const pollTimerRef  = useRef(null);
  const venueIdRef    = useRef(null);
  const prevPhaseRef  = useRef(null);


  // Get venue_id from checkin on mount + join game
  useEffect(() => {
    api.get('/checkin/my-checkin').then(r => {
      const vid = r.data?.checkin?.venue_id;
      if (!vid) { navigate('/home'); return; }
      setVenueId(vid);
      venueIdRef.current = vid;

      // Check first-time intro
      const seen = localStorage.getItem('game_intro_seen');
      if (!seen) {
        setIntroSeen(false);
      } else {
        setIntroSeen(true);
        joinAndPoll(vid);
      }
    }).catch(() => navigate('/home'));
    // eslint-disable-next-line
  }, []);

  function handleIntroConfirm(vid) {
    localStorage.setItem('game_intro_seen', '1');
    setIntroSeen(true);
    joinAndPoll(vid || venueId);
  }

  async function joinAndPoll(vid) {
    try {
      await api.post('/trivia/join', { venue_id: vid });
    } catch (_) {}
    startPolling(vid);
  }

  // ---------------------------------------------------------------------------
  // Polling loop — 1 second interval
  // ---------------------------------------------------------------------------
  const startPolling = useCallback((vid) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(() => poll(vid), 1000);
    poll(vid); // immediate first call
  }, []); // eslint-disable-line

  useEffect(() => {
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, []);

  async function poll(vid) {
    try {
      const r = await api.get(`/trivia/state`, {
        params: { venue_id: vid, last_poll: lastPollRef.current }
      });
      const state = r.data;
      lastPollRef.current = state.server_now || Date.now();

      // Match toast detection
      if (state.new_match) {
        const nmId = state.new_match.match_id || '';
        if (!nmId || !sessionStorage.getItem('match_overlay_seen_' + nmId)) {
          if (nmId) sessionStorage.setItem('match_overlay_seen_' + nmId, '1');
          setMatchToast(state.new_match);
        }
      }

      // Reset answer state on new QUESTION phase
      if (state.phase === 'QUESTION' && prevPhaseRef.current !== 'QUESTION') {
        setMyAnswer(null);
      }

      // On BREAK start — load supporting data
if (state.phase === 'BREAK' && prevPhaseRef.current !== 'BREAK') {
        setBreakSubView('SOCIAL');
        setUserSelectedTab(null);
        fetchBreakData(vid);
      }

      // Fetch people for floating Say Hi button on non-BREAK phases
      if (['QUESTION','RESULT','LEADERBOARD'].includes(state.phase) &&
          !['QUESTION','RESULT','LEADERBOARD'].includes(prevPhaseRef.current)) {
        api.get('/trivia/people', { params: { venue_id: vid } })
          .then(r => setPeople(r.data)).catch(() => {});
      }

      prevPhaseRef.current = state.phase;
      setGameState(state);

      // If user already answered, reflect it
      if (state.already_answered && state.my_answer) {
        setMyAnswer(state.my_answer);
      }

    } catch (_) {}
  }

  async function fetchBreakData(vid) {
    try {
  const [lbRes, peopleRes, statsRes] = await Promise.all([
        api.get('/trivia/leaderboard', { params: { venue_id: vid } }),
        api.get('/trivia/people',      { params: { venue_id: vid } }),
        api.get('/trivia/stats',       { params: { venue_id: vid } })
      ]);
      setLeaderboard(lbRes.data);
setPeople(peopleRes.data);
      setMyStats(statsRes.data);
    } catch (_) {}
  }

  // BREAK sub-view timer (client-side only)
  useEffect(() => {
    if (!gameState || gameState.phase !== 'BREAK') return;
    const elapsed = (Date.now() - gameState.phase_start_at);
    if (elapsed < 40000)       setBreakSubView('SOCIAL');
    else if (elapsed < 80000)  setBreakSubView('STATS');
    else                       setBreakSubView('LEADERBOARD');
  }, [gameState]);

  // ---------------------------------------------------------------------------
  // Submit answer
  // ---------------------------------------------------------------------------
  async function submitAnswer(letter) {
    if (myAnswer || submitting || !gameState) return;
    setMyAnswer(letter);
    setSubmitting(true);
    try {
      await api.post('/trivia/answer', {
        venue_id:       venueId,
        answer:         letter,
        absolute_round: gameState.absolute_round
      });
    } catch (_) {
      // Silent — server handles duplicates
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Send Hi
  // ---------------------------------------------------------------------------
  async function sendHi(toUserId) {
    setHiSending(p => ({ ...p, [toUserId]: true }));
    try {
      await api.post('/like', { likedUserId: toUserId });
      // Refresh people list to update hi_status
      const r = await api.get('/trivia/people', { params: { venue_id: venueId } });
      setPeople(r.data);
    } catch (_) {}
    setHiSending(p => ({ ...p, [toUserId]: false }));
  }

  // ---------------------------------------------------------------------------
  // Time remaining (uses server clock)
  // ---------------------------------------------------------------------------
  function timeRemaining() {
    if (!gameState?.phase_start_at || !gameState?.phase_duration_ms) return 0;
    const rem = (gameState.phase_start_at + gameState.phase_duration_ms) - Date.now();
    return Math.max(0, Math.ceil(rem / 1000));
  }

  function progressPct() {
    if (!gameState?.phase_start_at || !gameState?.phase_duration_ms) return 100;
    const elapsed = Date.now() - gameState.phase_start_at;
    return Math.max(0, Math.min(100, 100 - (elapsed / gameState.phase_duration_ms) * 100));
  }

  // ---------------------------------------------------------------------------
  // Intro overlay (first-time only)
  // ---------------------------------------------------------------------------
  if (!introSeen) {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.introBox}>
          <Typography sx={styles.introTitle}>🎮 How it works</Typography>
          <Box sx={styles.introLines}>
            <Typography sx={styles.introLine}>🟡 Answer each question in <b>10 seconds</b></Typography>
            <Typography sx={styles.introLine}><IconBolt size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Answer in <b>5 seconds</b> for a speed boost</Typography>
            <Typography sx={styles.introLine}><IconFlame size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Keep a streak going for <b>even more points</b></Typography>
          </Box>
          <Button sx={styles.primaryBtn} onClick={() => handleIntroConfirm(venueIdRef.current)}>
            Let's Play!
          </Button>
        </Box>
      </Box>
    );
  }

  if (!gameState) {
    return (
      <Box sx={styles.root}>
        <CircularProgress  />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box sx={styles.root}>
      {/* Pill switcher */}
      <Box sx={styles.pillRow}>
        <Box
          sx={{ ...styles.pill, ...(activeTab === 'TRIVIA' ? styles.pillActive : {}) }}
          onClick={() => setActiveTab('TRIVIA')}
        >
          🎮 Trivia
        </Box>
        <Box
          sx={{ ...styles.pill, ...(activeTab === 'TOURNAMENT' ? styles.pillActive : {}) }}
          onClick={() => setActiveTab('TOURNAMENT')}
        >
          🏆 Tournament
        </Box>
      </Box>

      {/* Tournament info strip — always visible on Trivia tab */}
      {activeTab === 'TRIVIA' && (
        <TournamentInfoStrip upcoming={gameState?.tournament_upcoming} onSwitchTab={() => setActiveTab('TOURNAMENT')} />
      )}

      {/* Trivia tab */}
      {activeTab === 'TRIVIA' && (
        <>
          {gameState.phase === 'WAITING'     && <WaitingView state={gameState} />}
          {gameState.phase === 'QUESTION'    && (
            <QuestionView
              state={gameState}
              myAnswer={myAnswer}
              onAnswer={submitAnswer}
              timeRemaining={timeRemaining()}
              progressPct={progressPct()}
            />
          )}
          {gameState.phase === 'RESULT'      && <ResultView state={gameState} myAnswer={myAnswer} />}
          {gameState.phase === 'LEADERBOARD' && <LeaderboardPhaseView venueId={venueId} state={gameState} />}
          {gameState.phase === 'BREAK'       && (
            <BreakView
              subView={userSelectedTab || breakSubView}
              onTabSelect={setUserSelectedTab}
              state={gameState}
              leaderboard={leaderboard}
              people={people}
              stats={myStats}
              onHi={sendHi}
              hiSending={hiSending}
              timeRemaining={timeRemaining()}
            />
          )}
        </>
      )}

      {/* Tournament tab */}
      {activeTab === 'TOURNAMENT' && (
        <TournamentTab venueId={venueId} />
      )}

      {/* Match overlay */}
      <MatchOverlay
        visible={!!matchToast}
        myAlias={localStorage.getItem('user_alias') || ''}
        theirAlias={matchToast?.alias || ''}
        onChat={() => {
          navigate(`/chat/${matchToast.match_id}?from=game`);
          setMatchToast(null);
        }}
        onDismiss={() => setMatchToast(null)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// WAITING phase
// ---------------------------------------------------------------------------
function WaitingView({ state }) {
  return (
    <Box sx={styles.phaseBox}>
      <Typography sx={styles.emoji}>⏳</Typography>
      <Typography sx={styles.phaseTitle}>Waiting for players…</Typography>
      <Typography sx={styles.phaseSub}>
        Be the first one here tonight 🎮
      </Typography>
      <Typography sx={{ color: C.textDisabled, mt: 2, fontSize: '0.8rem' }}>
        Game starts when at least 1 player is ready
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// QUESTION phase
// ---------------------------------------------------------------------------
function QuestionView({ state, myAnswer, onAnswer, timeRemaining, progressPct }) {
  const q = state.question;
  if (!q) return null;

  const urgent = timeRemaining <= 3;

  return (
    <Box sx={styles.phaseBox}>
      <Box sx={styles.questionMeta}>
        <Chip label={q.category?.replace(/_/g, ' ')} size="small" sx={styles.catChip} />
        <Typography sx={styles.roundLabel}>
          Round {state.current_round} / 10
        </Typography>
      </Box>

      {/* Timer bar */}
      <Box sx={styles.timerRow}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            ...styles.timerBar,
            '& .MuiLinearProgress-bar': {
              background: urgent ? C.orange : C.yellow,
              transition: 'none'
            }
          }}
        />
        <Typography sx={{ ...styles.timerNum, color: urgent ? C.orange : C.textPrimary }}>
          {timeRemaining}s
        </Typography>
      </Box>

      <Typography sx={styles.questionText}>{q.q}</Typography>

      {/* Answer options */}
      <Box sx={styles.optionsGrid}>
        {['A', 'B', 'C', 'D'].map(letter => {
          const selected = myAnswer === letter;
          return (
            <Button
              key={letter}
              sx={{
                ...styles.optionBtn,
                ...(selected ? styles.optionSelected : {}),
                ...(myAnswer && !selected ? styles.optionDimmed : {})
              }}
              onClick={() => onAnswer(letter)}
              disabled={!!myAnswer}
            >
              <span style={{ fontWeight: 700, marginRight: 8 }}>{letter}</span>
              {q.options[letter]}
            </Button>
          );
        })}
      </Box>

      {myAnswer && (
        <Typography sx={styles.waitingText}>
          ⏳ Waiting for result…
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RESULT phase
// ---------------------------------------------------------------------------
function ResultView({ state, myAnswer }) {
  const q      = state.question;
  const result = state.my_result;
  const noAnswer  = !result || result.answer === null;
  const isCorrect = !noAnswer && result.is_correct === 1;
  const earned    = result?.score_earned ?? 0;
  const speedPts  = isCorrect && result?.speed_bonus  ? 5 : 0;
  const streakPts = isCorrect && result?.streak_bonus ? 5 : 0;
  const basePts   = isCorrect ? Math.max(0, earned - speedPts - streakPts) : 0;

  // hooks must be before any early return
  const [pops, setPops] = React.useState({ base: 0, speed: 0, streak: 0 });
  React.useEffect(() => {
    if (!isCorrect) return;
    setPops(p => ({ ...p, base: p.base + 1 }));
    if (speedPts)  setTimeout(() => setPops(p => ({ ...p, speed:  p.speed  + 1 })), 310);
    if (streakPts) setTimeout(() => setPops(p => ({ ...p, streak: p.streak + 1 })), 470);
  // eslint-disable-next-line
  }, []);

  if (!q) return null;

  return (
    <Box sx={styles.phaseBox}>
      <Box sx={{ mb: 1 }}>
        {noAnswer ? <IconNoAnswer size={48} /> : isCorrect ? <IconCorrect size={48} /> : <IconWrong size={48} />}
      </Box>


      {/* Question + correct answer */}
      <Typography sx={styles.questionText}>{q.q}</Typography>
      <Box sx={styles.optionsGrid}>
        {['A', 'B', 'C', 'D'].map(letter => {
          const isCor   = letter === q.correct;
          const wasMine = letter === (result?.answer || myAnswer);
          return (
            <Box key={letter} sx={{
              ...styles.resultOption,
              ...(isCor ? styles.resultCorrect : {}),
              ...(wasMine && !isCor ? styles.resultWrong : {})
            }}>
              <span style={{ fontWeight: 700, marginRight: 8 }}>{letter}</span>
              {q.options[letter]}
              {isCor && <IconTick size={16} style={{ marginLeft: 8 }} />}
            </Box>
          );
        })}
      </Box>

      {/* Animated score card — pops float up from here */}
      <Box sx={{ position: 'relative', width: '100%', mt: 2 }}>
        <ScorePops triggers={pops} />
        <ResultScoreCard
          isCorrect={isCorrect}
          noAnswer={noAnswer}
          basePts={basePts || 10}
          speedPts={speedPts}
          streakPts={streakPts}
        />
      </Box>

      <Typography sx={styles.phaseSub}>
        {state.correct_count} of {state.total_answered_count} players got it right
      </Typography>
      {(result?.streak_count ?? 0) >= 2 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <StreakBar streak={result.streak_count} />
        </Box>
      )}
    </Box>
  );
}

function ScoreLine({ label, value, highlight, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography sx={{ color: highlight ? C.yellow : C.textMuted, fontSize: '0.85rem', fontWeight: bold ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography sx={{ color: highlight ? C.yellow : C.textPrimary, fontSize: '0.85rem', fontWeight: bold ? 700 : 400 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// LEADERBOARD phase (between rounds)
// ---------------------------------------------------------------------------
function LeaderboardPhaseView({ venueId, state }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/trivia/leaderboard', { params: { venue_id: venueId } })
      .then(r => setData(r.data))
      .catch(() => {});
  }, [venueId, state.absolute_round]);

  return (
    <Box sx={styles.phaseBox}>
      <Box sx={{ mb: 1.5, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}><IconTrophy size={22} /><Typography sx={styles.phaseTitle}>After Round {state.current_round}</Typography></Box>
        <Typography sx={{ fontSize: '0.72rem', color: C.textDisabled, mt: 0.25 }}>Last 10 rounds</Typography>
      </Box>
      {!data ? <CircularProgress size={24} /> : (
        <LeaderboardTable entries={data.entries} myRank={data.my_rank} />
      )}
      <Typography sx={styles.phaseSub}>Next question coming up…</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BREAK phase — 3 sub-views
// ---------------------------------------------------------------------------
function BreakView({ subView, onTabSelect, state, leaderboard, people, stats, onHi, hiSending, timeRemaining }) {
  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      {/* Sub-view nav pills */}
      <Box sx={styles.breakNav}>
        {['SOCIAL', 'STATS', 'LEADERBOARD'].map(v => (
          <Typography key={v} onClick={() => onTabSelect(v)} sx={{
            ...styles.breakNavItem,
            ...(subView === v ? styles.breakNavActive : {})
          }}>
            {v === 'SOCIAL' ? <><IconWave size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Say Hi</> : v === 'STATS' ? <><IconStats size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Stats</> : <><IconTrophy size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Top Players</>}
          </Typography>
        ))}
      </Box>

      <Typography sx={{ color: C.textMuted, fontSize: '1.1rem', textAlign: 'center', mb: 2 }}>
        Break ends in {timeRemaining}s
      </Typography>

      {subView === 'SOCIAL'      && <SocialView people={people} state={state} onHi={onHi} hiSending={hiSending} />}
      {subView === 'STATS'       && <StatsView stats={stats} />}
      {subView === 'LEADERBOARD' && <LeaderboardTable entries={leaderboard?.entries} myRank={leaderboard?.my_rank} />}
    </Box>
  );
}

function SocialView({ people, state, onHi, hiSending }) {
  const [drawerPerson, setDrawerPerson] = React.useState(null);
  if (!people) return <CircularProgress sx={{ display: "block", mx: "auto" }} size={24} />;
  const list = people.people || [];

  function glassesLabel(person) {
    if (person.wearing_glasses_now) return 'Wearing glasses';
    if (person.profile_glasses === 1 || person.profile_glasses === true) return 'Usually wears glasses';
    return null;
  }

  function identifierLines(person) {
    const lines = [];
    if (person.location_area)      lines.push({ icon: '📍', text: person.location_area });
    const clothing = [person.clothing_primary, person.clothing_secondary].filter(Boolean).join(', ');
    if (clothing)                  lines.push({ icon: '👕', text: clothing });
    const hair = [person.hair_type, person.facial_hair].filter(Boolean).join(', ');
    const glasses = glassesLabel(person);
    const lookLine = [hair, glasses].filter(Boolean).join(', ');
    if (lookLine)                  lines.push({ icon: '✨', text: lookLine });
    if (person.accessories)        lines.push({ icon: '🎒', text: person.accessories });
    if (person.group_status)       lines.push({ icon: '👥', text: person.group_status });
    return lines;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>
          Players this round
        </Typography>
        <Chip
          label={`${people.likes_remaining ?? 0} Wave${people.likes_remaining !== 1 ? 's' : ''} remaining`}
          size="small"
          sx={styles.chip}
        />
      </Box>

      {list.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: C.textDisabled, fontSize: '0.9rem' }}>
            You're the first one here tonight 🎮
          </Typography>
          <Typography sx={{ color: C.textDisabled, fontSize: '0.8rem', mt: 1 }}>
            Invite someone to play with you
          </Typography>
        </Box>
      ) : (
        list.map(person => (
          <Box key={person.user_id} sx={{
            ...styles.personRow,
            opacity: person.is_active ? 1 : 0.45
          }}>
            <Box sx={styles.personInfo} onClick={() => setDrawerPerson(person)} style={{ cursor: 'pointer' }}>
              <Typography sx={styles.personAlias}>{person.alias}</Typography>
              <Typography sx={styles.personMeta}>
                {[person.location_area, person.clothing_primary].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Button
              size="small"
              disabled={
                person.hi_status !== 'can_hi' ||
                (people.likes_remaining ?? 0) === 0 ||
                hiSending[person.user_id]
              }
              sx={{
                ...styles.hiBtn,
                ...(person.hi_status === 'already_matched' ? styles.hiBtnMatched : {}),
                ...(person.hi_status === 'already_liked'   ? styles.hiBtnSent    : {})
              }}
              onClick={() => onHi(person.user_id)}
            >
              {person.hi_status === 'already_matched' ? '✓ Matched'
               : person.hi_status === 'already_liked'  ? '✓ Sent'
               : hiSending[person.user_id]             ? '…'
               : '👋 Wave'}
            </Button>
          </Box>
        ))
      )}

      {/* Profile drawer */}
      <Drawer
        anchor="bottom"
        open={!!drawerPerson}
        onClose={() => setDrawerPerson(null)}
        PaperProps={{ sx: { bgcolor: C.bgCard, borderRadius: '16px 16px 0 0', px: 3, py: 3 } }}
      >
        {drawerPerson && (() => {
          const p = drawerPerson;
          const lines = identifierLines(p);
          return (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>
                    {p.alias}{p.age_group ? `, ${p.age_group}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  disabled={
                    p.hi_status !== 'can_hi' ||
                    (people.likes_remaining ?? 0) === 0 ||
                    hiSending[p.user_id]
                  }
                  sx={{
                    ...styles.hiBtn,
                    ...(p.hi_status === 'already_matched' ? styles.hiBtnMatched : {}),
                    ...(p.hi_status === 'already_liked'   ? styles.hiBtnSent    : {})
                  }}
                  onClick={() => { onHi(p.user_id); setDrawerPerson(null); }}
                >
                  {p.hi_status === 'already_matched' ? '✓ Matched'
                   : p.hi_status === 'already_liked'  ? '✓ Sent'
                   : hiSending[p.user_id]             ? '…'
                   : '👋 Wave'}
                </Button>
              </Box>
              <Divider sx={{ borderColor: C.border, mb: 2 }} />
              {lines.map((l, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  {l.icon === '📍'
                    ? <IconLocation size={13} style={{ flexShrink: 0 }} />
                    : <Typography sx={{ fontSize: '0.95rem' }}>{l.icon}</Typography>}
                  <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>{l.text}</Typography>
                </Box>
              ))}
            </Box>
          );
        })()}
      </Drawer>
    </Box>
  );
}


function StatsView({ stats }) {
  if (!stats?.stats) return <CircularProgress sx={{ display: "block", mx: "auto" }} size={24} />;
  const s = stats.stats.session;
  const l = stats.stats.lifetime;

  return (
    <Box>
      <Typography sx={styles.statsSection}>This Session</Typography>
      <StatRow label="Rounds played"  value={s.rounds_played} />
      <StatRow label="Correct answers" value={`${s.correct_count} (${s.accuracy_pct}%)`} />
      <StatRow label="Best streak"    value={`${s.best_streak} 🔥`} />
      <StatRow label="Score"          value={formatScore(s.total_score)} />
      <StatRow label="Hi sent"        value={s.hi_sent} />

      <Divider sx={{ borderColor: C.border, my: 2 }} />

      <Typography sx={styles.statsSection}>All Time</Typography>
      <StatRow label="Total score"    value={l.total_score_display} bold />
      <StatRow label="Rounds played"  value={l.rounds_played} />
    </Box>
  );
}

function StatRow({ label, value, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>{label}</Typography>
      <Typography sx={{ color: bold ? C.textPrimary : C.textMuted, fontSize: '0.85rem', fontWeight: bold ? 700 : 400 }}>
        {value}
      </Typography>
    </Box>
  );
}

function LeaderboardTable({ entries, myRank }) {
  if (!entries) return <CircularProgress sx={{ display: "block", mx: "auto" }} size={24} />;

  // Get current user's alias to highlight row
  const myAlias = localStorage.getItem('user_alias');

  return (
    <Box>
      {entries.map((e, i) => {
        const isMe   = e.alias === myAlias;
        const isAway = e.source === 'away';
        return (
          <Box key={e.user_id || i} sx={{
            ...styles.lbRow,
            ...(isMe ? styles.lbRowMe : {}),
            ...(isAway ? { opacity: 0.7 } : {}),
          }}>
            <Typography sx={styles.lbRank}>
              {isAway ? '\u2014' : e.rank === 1 ? <IconMedal rank={1} size={20} /> : e.rank === 2 ? <IconMedal rank={2} size={20} /> : e.rank === 3 ? <IconMedal rank={3} size={20} /> : `#${e.rank}`}
            </Typography>
            <Typography sx={{ ...styles.lbAlias, ...(isAway ? { color: C.textMuted } : {}) }}>{e.alias}</Typography>
            <Box sx={isAway ? styles.lbBadgeAway : styles.lbBadgeHere}>
              <Typography sx={isAway
                ? { fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.03em', color: C.textDisabled }
                : { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em', color: C.teal }
              }>{isAway ? 'away' : 'HERE'}</Typography>
            </Box>
            <Typography sx={{ ...styles.lbScore, ...(isAway ? { color: C.textMuted, fontWeight: 400 } : {}) }}>{e.score_display}</Typography>
          </Box>
        );
      })}

      {/* Pinned row if outside top 10 */}
      {myRank && !myRank.in_top_10 && (
        <Box sx={{ ...styles.lbRow, ...styles.lbRowMe, mt: 1, borderTop: `1px dashed ${C.border}` }}>
          <Typography sx={styles.lbRank}>#{myRank.rank}</Typography>
          <Typography sx={styles.lbAlias}>You</Typography>
          <Box sx={styles.lbBadgeHere}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em' }}>HERE</Typography>
          </Box>
          <Typography sx={styles.lbScore}>{myRank.score_display}</Typography>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Tournament info strip — permanent banner on Trivia tab
// ---------------------------------------------------------------------------
function TournamentInfoStrip({ upcoming, onSwitchTab }) {
  if (!upcoming) return null;
  const start = new Date(upcoming.scheduled_start);
  const now = new Date();
  const timeStr = start.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  const isToday = start.toDateString() === now.toDateString();
  const line = isToday
    ? 'Tournament tonight at ' + timeStr
    : 'Tournament tomorrow at ' + timeStr;
  const isSignupOpen = upcoming.status === 'SIGNUP';

  if (isSignupOpen) {
    return (
      <Box sx={{
        width: '100%', maxWidth: 480,
        border: '1px solid rgba(251,188,21,0.5)',
        background: 'rgba(251,188,21,0.08)',
        borderRadius: 2, px: 2, py: 1.2, mb: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer'
      }} onClick={onSwitchTab}>
        <Box>
          <Typography sx={{ color: C.yellow, fontSize: '0.85rem', fontWeight: 700 }}>
            🏆 Tournament signup is open!
          </Typography>
          <Typography sx={{ color: C.textMuted, fontSize: '0.75rem', mt: 0.25 }}>
            Tap to register before it starts
          </Typography>
        </Box>
        <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.85rem' }}>
          Register →
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%', maxWidth: 480,
      border: '1px solid rgba(13,157,165,0.35)',
      background: 'rgba(13,157,165,0.07)',
      borderRadius: 2, px: 2, py: 1, mb: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <Typography sx={{ color: C.teal, fontSize: '0.82rem', fontWeight: 600 }}>
        🏆 {line}
      </Typography>
      <Typography sx={{ color: C.textDisabled, fontSize: '0.75rem' }}>
        Every 30 min
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  root: {
    minHeight: '100vh',
    background: C.bgApp,
    display: 'flex', flexDirection: 'column', alignItems: 'stretch',
    maxWidth: 600, mx: 'auto',
    px: 2, pt: 1, pb: 4
  },
  pillRow: {
    display: 'flex', gap: 1, width: '100%', maxWidth: 600, mb: 2, mt: 1
  },
  pill: {
    flex: 1, textAlign: 'center', py: 1, borderRadius: 5,
    fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
    background: 'rgba(255,255,255,0.05)', color: C.textMuted,
    border: '1px solid transparent',
    userSelect: 'none'
  },
  pillActive: {
    background: 'rgba(251,188,21,0.12)', color: C.yellow,
    border: `1px solid rgba(251,188,21,0.35)`
  },
  topBar: {
    display: 'flex', alignItems: 'center', width: '100%',
    maxWidth: 600, mb: 1, gap: 1
  },
  headerScore: { color: C.textPrimary, fontWeight: 700, fontSize: '1.1rem', flexGrow: 1 },
  streakChip: { background: 'rgba(254,114,16,0.12)', color: C.orange, border: `1px solid rgba(254,114,16,0.3)` },
  phaseBox: {
    width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    pt: 2, px: 0, mx: -2
  },
  emoji: { fontSize: '3rem', mb: 1 },
  phaseTitle: { color: C.textPrimary, fontWeight: 700, fontSize: '1.3rem', mb: 0, textAlign: 'center' },
  phaseSub:   { color: C.textMuted, fontSize: '0.85rem', mt: 2, textAlign: 'center' },
  questionMeta: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', width: '100%', mb: 1
  },
  catChip: { background: 'rgba(13,157,165,0.12)', color: C.teal, fontSize: '0.7rem' },
  roundLabel: { color: C.textDisabled, fontSize: '0.8rem' },
  timerRow: { display: 'flex', alignItems: 'center', width: '100%', gap: 1, mb: 2 },
  timerBar: { flexGrow: 1, height: 8, borderRadius: 4, background: C.bgElevated },
  timerNum: { fontWeight: 700, minWidth: 30, textAlign: 'right', fontSize: '0.9rem' },
  questionText: {
    color: C.textPrimary, fontSize: '1.1rem', fontWeight: 600,
    textAlign: 'center', mb: 2.5, lineHeight: 1.5
  },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' },
  optionBtn: {
    background: C.bgCard, color: C.textMuted, border: `1px solid ${C.border}`,
    borderRadius: 2, py: 1.5, px: 2, textAlign: 'left',
    justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.9rem',
    '&:hover': { background: C.bgElevated, borderColor: C.yellow }
  },
  optionSelected: { background: 'rgba(251,188,21,0.12)', borderColor: C.yellow, color: C.yellow },
  optionDimmed:   { opacity: 0.4 },
  waitingText:    { color: C.textDisabled, fontSize: '0.8rem', mt: 2 },
  resultOption: {
    display: 'flex', alignItems: 'center',
    background: C.bgCard, color: C.textMuted,
    border: `1px solid ${C.border}`, borderRadius: 2,
    py: 1.5, px: 2, fontSize: '0.9rem', width: '100%'
  },
  resultCorrect: { background: 'rgba(34,197,94,0.1)', color: C.success, borderColor: C.success },
  resultWrong:   { background: 'rgba(254,114,16,0.1)', color: C.orange, borderColor: C.orange },
  scoreBreakdown: {
    background: C.bgApp, borderRadius: 2, p: 2, width: '100%', mt: 2
  },
  breakNav: {
    display: 'flex', justifyContent: 'space-around',
    borderBottom: `1px solid ${C.border}`, mb: 2, width: '100%'
  },
  breakNavItem: {
    color: C.textDisabled, fontSize: '0.8rem', pb: 1, cursor: 'default'
  },
  breakNavActive: { color: C.textPrimary, borderBottom: `2px solid ${C.yellow}` },
  personRow: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    background: C.bgCard, borderRadius: 2, p: 1.5, mb: 1
  },
  personInfo:  { flexGrow: 1 },
  personAlias: { color: C.textPrimary, fontWeight: 600, fontSize: '0.9rem' },
  personMeta:  { color: C.textDisabled, fontSize: '0.75rem', mt: 0.25 },
  hiBtn: {
    background: 'rgba(251,188,21,0.1)', color: C.yellow,
    border: `1px solid rgba(251,188,21,0.3)`, borderRadius: 5,
    fontSize: '0.8rem', px: 1.5, py: 0.5, minWidth: 70,
    '&:disabled': { opacity: 0.4 }
  },
  hiBtnSent:    { color: C.textMuted, border: `1px solid ${C.border}`, background: 'transparent' },
  hiBtnMatched: { color: C.success, border: `1px solid rgba(34,197,94,0.3)`, background: 'rgba(34,197,94,0.08)' },
  chip: { background: 'rgba(251,188,21,0.1)', color: C.yellow, fontSize: '0.75rem', fontWeight: 600 },
  statsSection: { color: C.textDisabled, fontSize: '0.75rem', letterSpacing: 1, mb: 1.5, textTransform: 'uppercase' },
  lbRow: {
    display: 'flex', alignItems: 'center', gap: 1.5,
    background: C.bgCard, borderRadius: 2, px: 2, py: 1, mb: 0.5
  },
  lbRowMe:  { background: 'rgba(251,188,21,0.08)', border: `1px solid rgba(251,188,21,0.25)` },
  lbRank:   { width: 32, flexShrink: 0, fontSize: '1rem', fontWeight: 700, color: C.textPrimary, textAlign: 'center' },
  lbAlias:  { flexGrow: 1, minWidth: 0, color: C.textPrimary, fontSize: '0.95rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  lbScore:  { color: C.yellow, fontWeight: 700, fontSize: '1rem', width: 48, textAlign: 'right', flexShrink: 0 },
  lbBadgeHere: { flexShrink: 0, background: 'rgba(13,157,165,0.15)', border: `1px solid ${C.teal}`, borderRadius: 1, px: 0.7, py: 0.15 },
  lbBadgeAway: { flexShrink: 0, px: 0.5, py: 0.15 },
  introBox: {
    background: C.bgCard, border: `1px solid ${C.border}`,
    borderRadius: 3, p: 4, maxWidth: 520, width: '90%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2
  },
  introTitle: { color: C.textPrimary, fontWeight: 700, fontSize: '1.4rem' },
  introLines: { display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' },
  introLine:  { color: C.textMuted, fontSize: '0.95rem' },
  primaryBtn: {
    background: C.yellow, color: C.yellowText, fontWeight: 700,
    borderRadius: 5, px: 4, py: 1.2, mt: 1,
    '&:hover': { background: '#e6aa10' }
  }
};